import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "bpmn-js/dist/assets/diagram-js.css";
import type { IWorkFlowVersionDto } from "@kuika/kuika-cl-model/src/dtos/workflow.dto";
import type { AxiosResponse } from "axios";
import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import type { ModuleDeclaration } from "didi";
import React, { useEffect, useRef, useState } from "react";
import { WorkFlowService } from "../../../../services/workflow-service";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import customModdleExtension from "../moddle/custom.json";
import customWFSharedElementsModule from "../moddle/customWFSharedElementsModule";
import { IVersionRow, versionLabel } from "./version-history";
import "./version-diagram-modal.scss";

/** Same width as the process-template details modal — the diagram needs the room. */
const MODAL_WIDTH: number = 810;

/**
 * How far the version's XML got. `missing` and `failed` are separate because they are different
 * facts about the version — one has no diagram, the other could not be read — and telling the
 * user which is why this modal never shows a blank box.
 */
type VersionXmlStatus = "loading" | "ready" | "missing" | "failed";

interface IVersionXmlState {
  /**
   * The version this payload belongs to. Rendering is gated on it matching the open version, so
   * reopening the modal on a different row cannot flash the previous version's diagram.
   */
  versionId: string;
  status: VersionXmlStatus;
  xml: string;
}

const INITIAL_STATE: IVersionXmlState = { versionId: "", status: "loading", xml: "" };

/** Minimal slice of the bpmn-js canvas service used here (avoids `any`). */
interface IBpmnCanvas {
  zoom(mode: string, center: string): void;
}

interface IVersionDiagramProps {
  xml: string;
}

/**
 * The version's BPMN, read-only and zoomed to fit.
 *
 * `NavigatedViewer` (not `Modeler`) is read-only by construction: no palette, no context pad,
 * nothing to edit. It is preferred over the plain `Viewer` here because a real process version can
 * be far larger than the modal, and fit-to-viewport alone would leave it unreadable — this adds
 * scroll/zoom only, which is also what the compare panes use. The custom moddle extension and
 * shared elements module are the same pair
 * the editor's own read-only viewer registers (`bpmn.service.createViewer`), so a version renders
 * exactly as the editor draws it instead of falling back to stock BPMN shapes.
 *
 * Lifecycle: this component is only mounted once the XML has arrived, and KModal renders through
 * rc-dialog with `destroyOnClose`, so it — and its container element — exist only while the modal
 * is open on a diagram. The viewer is therefore created on mount (the container ref is attached
 * before the effect runs) and destroyed by the effect cleanup when the XML changes, the modal
 * closes or the tool unmounts. At most one viewer is alive at a time, so an open/close cycle
 * cannot accumulate them, and a double-invoked effect destroys its first instance before the
 * second is created.
 */
const VersionDiagram = ({ xml }: IVersionDiagramProps): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect((): (() => void) | undefined => {
    const container: HTMLDivElement | null = containerRef.current;
    if (!container) return undefined;

    let cancelled: boolean = false;
    const viewer: NavigatedViewer = new NavigatedViewer({
      container,
      width: "100%",
      height: "100%",
      moddleExtensions: { custom: customModdleExtension },
      // A valid didi module whose inferred literal type doesn't structurally match
      // ModuleDeclaration, so cast through unknown (mirrors how bpmn.service registers it).
      additionalModules: [customWFSharedElementsModule() as unknown as ModuleDeclaration]
    });

    viewer
      .importXML(xml)
      .then((): void => {
        if (cancelled) return;
        const canvas: IBpmnCanvas = viewer.get("canvas");
        canvas.zoom("fit-viewport", "auto");
      })
      .catch((): void => {
        // Stored XML that bpmn-js refuses leaves the empty canvas surface behind. There is
        // nothing to recover from and the service layer already reports transport failures.
      });

    return (): void => {
      cancelled = true;
      try {
        viewer.destroy();
      } catch {
        // Tearing down after a failed import can throw; the viewer is gone either way.
      }
    };
  }, [xml]);

  return <div ref={containerRef} className="kuika-version-diagram__canvas" />;
};

interface IVersionDiagramUnavailableProps {
  status: VersionXmlStatus;
}

/** Honest stand-in for the diagram: says which of the two failure facts happened. */
const VersionDiagramUnavailable = ({ status }: IVersionDiagramUnavailableProps): React.ReactElement => {
  const isMissing: boolean = status === "missing";
  const title: string = isMissing ? "This version has no saved diagram" : "This version's diagram could not be loaded";
  const description: string = isMissing
    ? "It was exported without a BPMN diagram, so there is nothing to display."
    : "The request for this version did not return its diagram. Close the dialog and try again.";
  return <KuikaEmptyState variant="page" svgName="automation" customName={title} description={description} />;
};

export interface IVersionDiagramModalProps {
  /** The version whose diagram is open; `undefined` keeps the modal closed. */
  version?: IVersionRow;
  onClose: () => void;
}

/**
 * One exported version's BPMN diagram, read-only.
 *
 * Opened from the Actions column of the Version History table. The version row already carries
 * everything the header needs; only the XML is fetched, keyed on the version id.
 */
const VersionDiagramModal = ({ version, onClose }: IVersionDiagramModalProps): React.ReactElement => {
  const versionId: string | undefined = version?.id;
  const [state, setState] = useState<IVersionXmlState>(INITIAL_STATE);

  useEffect((): (() => void) | undefined => {
    if (!versionId) return undefined;

    let cancelled: boolean = false;
    WorkFlowService.GetVersion(versionId)
      .then((response: AxiosResponse<IWorkFlowVersionDto>): void => {
        if (cancelled) return;
        // The service reports a failed request by RESOLVING with the error object, so neither
        // the response nor its data is guaranteed despite the Axios typing.
        const dto: IWorkFlowVersionDto | undefined = response?.data;
        const xml: string = dto?.xml?.trim() ?? "";
        if (!dto) {
          setState({ versionId, status: "failed", xml: "" });
          return;
        }
        setState({ versionId, status: xml ? "ready" : "missing", xml });
      })
      .catch((): void => {
        if (cancelled) return;
        setState({ versionId, status: "failed", xml: "" });
      });

    return (): void => {
      cancelled = true;
    };
  }, [versionId]);

  // A payload from a previous version is not this version's answer — treat it as still loading.
  const isCurrent: boolean = !!versionId && state.versionId === versionId;
  const status: VersionXmlStatus = isCurrent ? state.status : "loading";
  const xml: string = isCurrent ? state.xml : "";

  return (
    <KModal
      className="kuika-version-diagram"
      showModal={!!version}
      closeModal={onClose}
      header={version ? versionLabel(version) : ""}
      isLoading={status === "loading"}
      width={MODAL_WIDTH}
    >
      <div className="kuika-version-diagram__body" data-testid="processVersionDiagram">
        {/* The frame keeps its height in every state, so fit-to-viewport measures a stable box
            and the modal neither collapses under the spinner nor jumps when the XML lands. */}
        <div className="kuika-version-diagram__frame">
          {status === "ready" && <VersionDiagram xml={xml} />}
          {(status === "missing" || status === "failed") && <VersionDiagramUnavailable status={status} />}
        </div>
      </div>
    </KModal>
  );
};

export default VersionDiagramModal;
